import { FC, useState } from 'react';

import { ReactComponent as DownloadIcon } from '../../../assets/icons/download.svg';
import { useAppDispatch } from '../../../hooks';
import AttachmentPopup from '../../../pages/NewWork/components/AttachmentPopup';
import { getFileViewUrl } from '../../../reducers/work';
import { downloadFile } from '../../../services/utils';
import { Attachment } from '../../../types/work';
import { getFileIcon } from '../../../utils/fileTypeHelpers';
import { 
    AttachmentItem,
    AttachmentsContainer,
    AttachmentsGrid,
    AttachmentsHeader } from './styled';

type Props = {
    attachments: Attachment[];
    title?: string;
    showTitle?: boolean;
    withPadding?: boolean;
    withMargin?: boolean;
};

const AttachmentList: FC<Props> = ({ attachments, title = 'Attachments', showTitle = true, withPadding = true, withMargin = true }) => {
    const dispatch = useAppDispatch();

    const [ popupState, setPopupState ] = useState({
        isOpen: false,
        url: '',
        type: '',
        name: '',
        fileId: 0,
        currentIndex: 0
    });

    const getAttachmentUrl = async (file: Attachment, index: number) => {
        try {
            const response = await dispatch(getFileViewUrl({ fileId: Number(file.id) })).unwrap();
            setPopupState({
                isOpen: true,
                url: response.url,
                type: file.type || '',
                name: file.name || '',
                fileId: Number(file.id),
                currentIndex: index
            });
        } catch (error) {
            console.error('Error fetching attachment URL:', error);
        }
    };

    const handleDownload = async (e: React.MouseEvent, file: Attachment) => {
        e.stopPropagation();

        try {
            const response = await dispatch(getFileViewUrl({ fileId: Number(file.id) })).unwrap();
            await downloadFile(response.url, file.name || 'download');

        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const closePopup = () => {
        setPopupState(prev => ({ ...prev, isOpen: false }));
    };

    const handleNextAttachment = async () => {
        if (attachments.length === 0) {
            return;
        }

        const nextIndex = 
            (popupState.currentIndex + 1) % attachments.length;
        const nextFile = attachments[nextIndex];

        await getAttachmentUrl(nextFile, nextIndex);
    };

    if (!attachments || attachments.length === 0) {
        return null;
    }

    return (
        <>
            <AttachmentPopup
                isOpen={popupState.isOpen}
                onClose={closePopup}
                url={popupState.url}
                type={popupState.type}
                name={popupState.name}
                fileId={popupState.fileId}
                currentIndex={popupState.currentIndex + 1}
                totalItems={attachments.length}
                onNext={handleNextAttachment}
            />
            <AttachmentsContainer withPadding={withPadding} withMargin={withMargin}>
                {showTitle && (
                    <AttachmentsHeader variant="subtitle2">
                        {title}
                    </AttachmentsHeader>
                )}
                <AttachmentsGrid>
                    {attachments.map((file, index) => (
                        <AttachmentItem
                            key={`attachment-${file.id}`}
                            onClick={() => getAttachmentUrl(file, index)}
                        >
                            {getFileIcon(file.type || '')}
                            <span>{file.name}</span>
                            <DownloadIcon 
                                width={16} 
                                height={16}
                                onClick={(e) => handleDownload(e, file)}
                                style={{ cursor: 'pointer' }}
                            />
                        </AttachmentItem>
                    ))}
                </AttachmentsGrid>
            </AttachmentsContainer>
        </>
    );
};

export default AttachmentList;

